import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Printer, MapPin, Phone, Instagram, Check } from 'lucide-react';
type PrintMode = 'front' | 'back' | 'both' | 'single' | null;
export function InfluencerInvitePage() {
  const [printMode, setPrintMode] = useState<PrintMode>(null);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode) {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode(null);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printClass = printMode ? `print-mode-${printMode}` : '';
  return <div className={`min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white flex flex-col items-center ${printClass}`} data-id="element-2138" data-mp="1vqxm129koyfl">
      {/* Controls - Hidden when printing */}
      <div className="max-w-[210mm] w-full mb-8 no-print" data-id="element-2139" data-mp="3q589b6airkj9">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6" data-id="element-2140" data-mp="3q4ozydyxq4gu">
          <div data-id="element-2141" data-mp="2j2rq7zaewta1">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2142" data-mp="2j289glwi0axl">
              Influencer Gift Certificate
            </h1>
            <p className="text-gray-600 text-sm" data-id="element-2143" data-mp="2rwen5sqcw2kt">
              Print each side separately as A5, or both together on A4
              Landscape.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center" data-id="element-2144" data-mp="205gweu7jus1z">
          <Button onClick={() => handlePrint('front')} className="gap-2 shadow-lg" data-id="element-2145" data-mp="2060bm35tf2cb">
            <Printer className="w-4 h-4" data-id="element-2146" data-mp="1v8gr4liixtbx" />
            Print Front Side
          </Button>
          <Button onClick={() => handlePrint('back')} className="gap-2 shadow-lg" data-id="element-2147" data-mp="297g9xcjcve06">
            <Printer className="w-4 h-4" data-id="element-2148" data-mp="249ermttr7ipr" />
            Print Back Side
          </Button>
          <Button onClick={() => handlePrint('both')} variant="outline" className="gap-2" data-id="element-2149" data-mp="3kneotx17su9b">
            <Printer className="w-4 h-4" data-id="element-2150" data-mp="2d9cgwby05kwu" />
            Print Both (A4 Landscape)
          </Button>
          <Button onClick={() => handlePrint('single')} variant="outline" className="gap-2" data-id="element-2151" data-mp="2i7e5fce76cfp">
            <Printer className="w-4 h-4" data-id="element-2152" data-mp="2maak4s0m9oed" />
            Print All on One Page (A4)
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3" data-id="element-2153" data-mp="2r6hqun0u73xd">
          <strong data-id="element-2154" data-mp="1uj4ha3tihcle">Front / Back:</strong> Set paper to{' '}
          <strong data-id="element-2155" data-mp="1ugi9c32pkv2n">A5 Portrait</strong>, margins <strong data-id="element-2156" data-mp="1yj56mji5f878">None</strong>, enable{' '}
          <strong data-id="element-2157" data-mp="1yk6kevqju8sn">Background graphics</strong>.<br data-id="element-2158" data-mp="23j9mq8f5a0wx" />
          <strong data-id="element-2159" data-mp="23irh4l6leacc">Both:</strong> Set paper to <strong data-id="element-2160" data-mp="23hgc6i1euwjn">A4 Landscape</strong>,
          margins <strong data-id="element-2161" data-mp="289gc4ahhdpg8">None</strong>, enable{' '}
          <strong data-id="element-2162" data-mp="28bavvlh0nbiq">Background graphics</strong>.
        </p>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FRONT SIDE CARD (Screen Preview + Print)   */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2163" data-mp="189w7s29w3se8">
        Front Side
      </div>
      <div className="front-panel bg-white shadow-2xl overflow-hidden relative mb-10 print-panel" style={{
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact'
    } as React.CSSProperties} data-id="element-2164" data-mp="13ajlpjquepcp">
        {/* Background Image */}
        <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2165" data-mp="pc3hq0kd7cy9" />

        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1]" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2166" data-mp="amnu2tg4rnms"></div>

        {/* Elegant Double Border Inset */}
        <div className="absolute inset-5 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.25)'
      }} data-id="element-2167" data-mp="25pk896u7v8r0"></div>
        <div className="absolute inset-7 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.10)'
      }} data-id="element-2168" data-mp="1wwgxx7evbc0l"></div>

        {/* Content */}
        <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-10 px-10 text-center" data-id="element-2169" data-mp="1mzcbr7l0vgjs">
          {/* Top Branding */}
          <div className="flex flex-col items-center" data-id="element-2170" data-mp="1914ipar3wzdw">
            <div className="rounded-xl p-3 mb-4" style={{
            backgroundColor: 'rgba(255,255,255,0.95)'
          }} data-id="element-2171" data-mp="1dz5z1nx5eklo">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 w-auto object-contain" data-id="element-2172" data-mp="38bvx8hujufyv" />
            </div>
            <span className="text-2xl font-bold tracking-tight leading-none mb-2" style={{
            color: '#ffffff',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }} data-id="element-2173" data-mp="3df6fayzihjma">
              DenticoClinic
            </span>
            <p className="text-sm uppercase leading-tight" style={{
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.3em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2174" data-mp="53aib6vo71pn">
              Private Dentistry
            </p>
          </div>

          {/* Center Content */}
          <div className="flex flex-col items-center w-full" data-id="element-2175" data-mp="2fu0o407p4put">
            <div className="w-12 h-px mb-7" style={{
            backgroundColor: 'rgba(255,255,255,0.4)'
          }} data-id="element-2176" data-mp="2brxbh89ajf7y"></div>

            <p className="text-xs uppercase font-medium mb-5" style={{
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.4em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2177" data-mp="etdxhlwdf8pz">
              Gift Certificate
            </p>

            <h2 className="text-4xl font-serif mb-8 leading-tight" style={{
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }} data-id="element-2178" data-mp="xqf41b5kpgw1">
              Professional
              <br data-id="element-2179" data-mp="17ggq5xed8p8b" />
              Teeth Whitening
            </h2>

            <div className="inline-block py-4 px-12" style={{
            borderTop: '1px solid rgba(255,255,255,0.35)',
            borderBottom: '1px solid rgba(255,255,255,0.35)'
          }} data-id="element-2180" data-mp="1cdqfld1r90w5">
              <p className="text-[10px] uppercase mb-2" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.3em'
            }} data-id="element-2181" data-mp="1ld3n406bjfe8">
                Value
              </p>
              <p className="text-3xl font-serif font-bold tracking-wide" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2182" data-mp="2wxcbhuyob913">
                £280
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-auto" data-id="element-2183" data-mp="3fvol1hthykx7">
            <p className="text-xs" style={{
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.2em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2184" data-mp="3fumz2iay0e0p">
              denticoclinic.co.uk
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BACK SIDE CARD (Screen Preview + Print)    */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2185" data-mp="3gp6anjbu6y2z">
        Back Side
      </div>
      <div className="back-panel overflow-hidden relative print-panel" style={{
      backgroundColor: '#fafaf9',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2186" data-mp="32uwxgt945xpb">
        {/* Elegant Border */}
        <div className="absolute inset-5 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.10)'
      }} data-id="element-2187" data-mp="3lmzg2a5bfo27"></div>
        <div className="absolute inset-7 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.05)'
      }} data-id="element-2188" data-mp="2a82niq9xagq3"></div>

        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col py-8 px-10 items-center text-center h-full" data-id="element-2189" data-mp="26pdd865jvvpt">
          {/* Top Branding */}
          <div className="flex flex-col items-center mb-5 mt-1" data-id="element-2190" data-mp="22ojpbcs3ygwx">
            <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-8 w-auto object-contain mb-1.5" data-id="element-2191" data-mp="2goudu95y3jex" />
            <span className="text-base font-bold tracking-tight leading-none mb-1" style={{
            color: '#111827'
          }} data-id="element-2192" data-mp="1nxkhi3z3jwee">
              Dentico
              <span style={{
              color: '#0d9488'
            }} data-id="element-2193" data-mp="1sz04i1ku4czf">
                Clinic
              </span>
            </span>
            <p className="text-[9px] uppercase leading-tight" style={{
            color: '#6b7280',
            letterSpacing: '0.2em'
          }} data-id="element-2194" data-mp="157cle5tte67p">
              Private Dentistry
            </p>
          </div>

          {/* Invitation Text */}
          <h1 className="text-xl font-serif mb-4 tracking-wide" style={{
          color: '#134e4a'
        }} data-id="element-2195" data-mp="3cshlgbs630rt">
            Exclusive Invitation
          </h1>

          <div className="max-w-[88%] space-y-3 mb-5" data-id="element-2196" data-mp="32yr0vhz5vahe">
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2197" data-mp="2yvumk3gnm0j9">
              We are delighted to offer you a complimentary professional teeth
              whitening experience at Dentico Dental Clinic.
            </p>
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2198" data-mp="2k7gcb7gwr9so">
              As part of our collaboration, we invite you to visit our clinic,
              experience our modern digital dentistry, and share your experience
              with your audience.
            </p>
          </div>

          {/* What's Included */}
          <div className="mb-5 w-full max-w-[85%]" data-id="element-2199" data-mp="uqukv8krnhav">
            <h3 className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{
            color: '#115e59'
          }} data-id="element-2200" data-mp="znl1bx0hgewt">
              What's Included
            </h3>
            <ul className="space-y-2 text-left inline-block" data-id="element-2201" data-mp="70rxh5rsqzil">
              {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-2202" data-mp="lp4tg2qvwid3" data-mpi={idx}>
                  <div className="mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#ccfbf1',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              } as React.CSSProperties} data-id="element-2203" data-mp="3ked2e4nkdez3">
                    <Check className="w-2 h-2" style={{
                  color: '#0f766e'
                }} data-id="element-2204" data-mp="3r18p8038r6ge" />
                  </div>
                  <span className="text-[13px]" style={{
                color: '#374151'
              }} data-id="element-2205" data-mp="2ye4oi7b6leaf">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Unique Code & Validity */}
          <div className="w-full max-w-[180px] mb-5" data-id="element-2206" data-mp="nll0d1f6jhhf">
            <div className="mb-3" data-id="element-2207" data-mp="nk06zf4lq4l5">
              <label className="block text-[9px] uppercase tracking-widest mb-1" style={{
              color: '#6b7280'
            }} data-id="element-2208" data-mp="nnebou0qmpvr">
                Unique Code
              </label>
              <div contentEditable suppressContentEditableWarning className="w-full text-sm font-mono text-center pb-1 min-h-[1.5em] focus:outline-none" style={{
              color: '#374151',
              borderBottom: '1px solid #d1d5db'
            }} data-id="element-2209" data-mp="25k99xq5hnk5e">
                DC-
              </div>
            </div>
            <p className="text-[10px] text-center tracking-wider" style={{
            color: '#6b7280'
          }} data-id="element-2210" data-mp="2o8mp0l2me2da">
              Valid until: _______________
            </p>
          </div>

          {/* Footer Section */}
          <div className="mt-auto w-full flex flex-col items-center" data-id="element-2211" data-mp="336ezgytb80km">
            <p className="text-[9px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2212" data-mp="3353zzb6wdezb">
              By appointment only • Non-transferable
            </p>

            {/* Address + Contact Combined */}
            <div className="flex flex-col items-center text-[10px] mb-4 leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2213" data-mp="1eevlk3bxz704">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2214" data-mp="15dxh5jxlcxh0">
                <MapPin className="w-2.5 h-2.5" style={{
                color: '#0d9488'
              }} data-id="element-2215" data-mp="221aft83dp4e7" />
                <span className="font-medium" style={{
                color: '#374151'
              }} data-id="element-2216" data-mp="1t9s1l2pzsynr">
                  Central London – Angel
                </span>
              </div>
              <span data-id="element-2217" data-mp="2lrw0gp8b3350">360–364 City Road, London EC1V 2PY</span>
              <div className="flex items-center justify-center gap-4 mt-1.5" data-id="element-2218" data-mp="2gzck5ki8htwi">
                <div className="flex items-center gap-1" data-id="element-2219" data-mp="2ob0oof4ylixi">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2220" data-mp="2ocljm5v6r3vw" />
                  <span data-id="element-2221" data-mp="2y85ecoy0jwj2">+44 7345 197820</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-2222" data-mp="32b1p5thbaocg">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2223" data-mp="328fidw28wcva" />
                  <span data-id="element-2224" data-mp="3c752zwlj0bf8">@dentico_clinic</span>
                </div>
              </div>
            </div>

            {/* Two QR Codes Row */}
            <div className="flex items-start justify-center gap-8 w-full" data-id="element-2225" data-mp="3lyj58c4v3pc2">
              {/* QR 1: Instagram */}
              <div className="flex flex-col items-center" data-id="element-2226" data-mp="3q0mklbunje2m">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2227" data-mp="3py1iqmucsy4v">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR Code" className="w-full h-full object-contain" data-id="element-2228" data-mp="1qwe4pukf3h9u" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2229" data-mp="2ej45ytoxz7te">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2230" data-mp="29ksu6df36n8j" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2231" data-mp="2im0uf115xy59">
                    Follow us
                  </p>
                </div>
              </div>

              {/* QR 2: WhatsApp */}
              <div className="flex flex-col items-center" data-id="element-2232" data-mp="2zwmsjd0686w4">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2233" data-mp="34v7k6o1cxi8y">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR Code" className="w-full h-full object-contain" data-id="element-2234" data-mp="2v2s11nebqnmh" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2235" data-mp="2c60jpjiceil2">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2236" data-mp="2c6k1n07zt6z3" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2237" data-mp="aedpza72g9kw">
                    via WhatsApp
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BOTH SIDES ON ONE PAGE (A4 Landscape Preview) */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 mt-6 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2238" data-mp="26ldhtuz5qr0l">
        Both Sides — One Page
      </div>
      <div className="single-panel overflow-hidden relative print-panel-single flex" style={{
      backgroundColor: '#ffffff',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2239" data-mp="21n259chha2eo">
        {/* Fold line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed z-50" style={{
        borderColor: 'rgba(200,200,200,0.4)'
      }} data-id="element-2240" data-mp="3dmsayfaqeak"></div>

        {/* LEFT — Front Side */}
        <div className="w-1/2 h-full relative flex flex-col items-center justify-center text-center" style={{
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2241" data-mp="c5x60ppeh1wk">
          <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2242" data-mp="3klgulbf4lw5a" />
          <div className="absolute inset-0 z-[1]" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact'
        } as React.CSSProperties} data-id="element-2243" data-mp="14uc7e9flzlpf"></div>
          <div className="absolute inset-4 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.25)'
        }} data-id="element-2244" data-mp="2vdsg4ldjv2fr"></div>
          <div className="absolute inset-6 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.10)'
        }} data-id="element-2245" data-mp="22p4nu5udvcsp"></div>

          <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-8 px-8" data-id="element-2246" data-mp="2cn2c2tc8t9lb">
            <div className="flex flex-col items-center" data-id="element-2247" data-mp="2goe1axr7fssz">
              <div className="rounded-xl p-2.5 mb-3" style={{
              backgroundColor: 'rgba(255,255,255,0.95)'
            }} data-id="element-2248" data-mp="2gpyvundirbqu">
                <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-10 w-auto object-contain" data-id="element-2249" data-mp="3s1zr8l4w1yfk" />
              </div>
              <span className="text-xl font-bold tracking-tight leading-none mb-1.5" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2250" data-mp="17muk89fmtdtu">
                DenticoClinic
              </span>
              <p className="text-xs uppercase leading-tight" style={{
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.3em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2251" data-mp="spbqsts0ecg2">
                Private Dentistry
              </p>
            </div>
            <div className="flex flex-col items-center w-full" data-id="element-2252" data-mp="2e3ojpljz4047">
              <div className="w-10 h-px mb-5" style={{
              backgroundColor: 'rgba(255,255,255,0.4)'
            }} data-id="element-2253" data-mp="2e2ddzg6ywiag"></div>
              <p className="text-[10px] uppercase font-medium mb-4" style={{
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '0.4em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2254" data-mp="320umqmopws6p">
                Gift Certificate
              </p>
              <h2 className="text-3xl font-serif mb-6 leading-tight" style={{
              color: '#ffffff',
              textShadow: '0 1px 4px rgba(0,0,0,0.3)'
            }} data-id="element-2255" data-mp="3kyxdb5wwyx6m">
                Professional
                <br data-id="element-2256" data-mp="3bzjzy25o5ogm" />
                Teeth Whitening
              </h2>
              <div className="inline-block py-3 px-10" style={{
              borderTop: '1px solid rgba(255,255,255,0.35)',
              borderBottom: '1px solid rgba(255,255,255,0.35)'
            }} data-id="element-2257" data-mp="1vi61xv570brq">
                <p className="text-[9px] uppercase mb-1.5" style={{
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.3em'
              }} data-id="element-2258" data-mp="1o0rdvhbve77a">
                  Value
                </p>
                <p className="text-2xl font-serif font-bold tracking-wide" style={{
                color: '#ffffff',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} data-id="element-2259" data-mp="19ak5jufku4kb">
                  £280
                </p>
              </div>
            </div>
            <div className="mt-auto" data-id="element-2260" data-mp="1e8lomfd063vs">
              <p className="text-[10px]" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.2em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2261" data-mp="qkkljlylffnr">
                denticoclinic.co.uk
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT — Back Side */}
        <div className="w-1/2 h-full relative flex flex-col py-6 px-8 items-center text-center" style={{
        backgroundColor: '#fafaf9',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2262" data-mp="196e5608bjn56">
          <div className="absolute inset-4 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.10)'
        }} data-id="element-2263" data-mp="1mwzpzuhc275p"></div>
          <div className="absolute inset-6 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.05)'
        }} data-id="element-2264" data-mp="1wvozayv5yjac"></div>

          <div className="relative z-10 flex flex-col items-center h-full w-full" data-id="element-2265" data-mp="2hxrm4wea9hj">
            {/* Logo */}
            <div className="flex flex-col items-center mb-4 mt-1" data-id="element-2266" data-mp="2frywhgvzxf4s">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-7 w-auto object-contain mb-1" data-id="element-2267" data-mp="2fublge068prc" />
              <span className="text-sm font-bold tracking-tight leading-none mb-0.5" style={{
              color: '#111827'
            }} data-id="element-2268" data-mp="221ve5108m5km">
                Dentico
                <span style={{
                color: '#0d9488'
              }} data-id="element-2269" data-mp="2upzq249ejevr">
                  Clinic
                </span>
              </span>
              <p className="text-[8px] uppercase leading-tight" style={{
              color: '#6b7280',
              letterSpacing: '0.2em'
            }} data-id="element-2270" data-mp="2kqs8f4vmwy2u">
                Private Dentistry
              </p>
            </div>

            <h1 className="text-lg font-serif mb-3 tracking-wide" style={{
            color: '#134e4a'
          }} data-id="element-2271" data-mp="1auls6xdp3kay">
              Exclusive Invitation
            </h1>

            <div className="max-w-[90%] space-y-2 mb-4" data-id="element-2272" data-mp="11scj8by2ryzo">
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2273" data-mp="16qxhw6xoiam0">
                We are delighted to offer you a complimentary professional teeth
                whitening experience at Dentico Dental Clinic.
              </p>
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2274" data-mp="j3pfbwrmxfno">
                As part of our collaboration, we invite you to visit our clinic,
                experience our modern digital dentistry, and share your
                experience with your audience.
              </p>
            </div>

            <div className="mb-4 w-full max-w-[85%]" data-id="element-2275" data-mp="3qmw0srqq8hmy">
              <h3 className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{
              color: '#115e59'
            }} data-id="element-2276" data-mp="3lo2o31j9kunu">
                What's Included
              </h3>
              <ul className="space-y-1.5 text-left inline-block" data-id="element-2277" data-mp="3vkfenqjiised">
                {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-2278" data-mp="n5du4nmoxgo4" data-mpi={idx}>
                    <div className="mt-0.5 w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: '#ccfbf1',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                } as React.CSSProperties} data-id="element-2279" data-mp="i6kf2qrr9hlu">
                      <Check className="w-1.5 h-1.5" style={{
                    color: '#0f766e'
                  }} data-id="element-2280" data-mp="23l5czfb02rvj" />
                    </div>
                    <span className="text-[11px]" style={{
                  color: '#374151'
                }} data-id="element-2281" data-mp="1ftq5z98a3mdy">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-end justify-center gap-6 mb-3" data-id="element-2282" data-mp="1tlwo30uzs3g1">
              <div data-id="element-2283" data-mp="x38gremnn8xc">
                <label className="block text-[8px] uppercase tracking-widest mb-0.5" style={{
                color: '#6b7280'
              }} data-id="element-2284" data-mp="x3rz2wg7vzbw">
                  Unique Code
                </label>
                <div contentEditable suppressContentEditableWarning className="text-xs font-mono text-center pb-0.5 min-h-[1.3em] focus:outline-none" style={{
                color: '#374151',
                borderBottom: '1px solid #d1d5db',
                minWidth: '70px'
              }} data-id="element-2285" data-mp="olyhjr8hdqnx">
                  DC-
                </div>
              </div>
              <p className="text-[9px] tracking-wider pb-0.5" style={{
              color: '#6b7280'
            }} data-id="element-2286" data-mp="5txcg7545462">
                Valid until: _______________
              </p>
            </div>

            <p className="text-[8px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2287" data-mp="3o1mqnvxmc3qt">
              By appointment only • Non-transferable
            </p>

            {/* Footer */}
            <div className="mt-auto w-full flex flex-col items-center" data-id="element-2288" data-mp="2s21utkncbbym">
              <div className="flex flex-col items-center text-[9px] mb-3 leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2289" data-mp="2ny2fh5dsqj6u">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-2290" data-mp="2wztook7jp85g">
                  <MapPin className="w-2 h-2" style={{
                  color: '#0d9488'
                }} data-id="element-2291" data-mp="298xzzvydyt2f" />
                  <span className="font-medium" style={{
                  color: '#374151'
                }} data-id="element-2292" data-mp="2iz93jhe5r08s">
                    Central London – Angel
                  </span>
                </div>
                <span data-id="element-2293" data-mp="1qh533qg74npf">360–364 City Road, London EC1V 2PY</span>
                <div className="flex items-center justify-center gap-3 mt-1" data-id="element-2294" data-mp="1lj3j93ay4kcw">
                  <div className="flex items-center gap-1" data-id="element-2295" data-mp="11kre9c0m5vo">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2296" data-mp="109kw1r6qk0v" />
                    <span data-id="element-2297" data-mp="3o4pfkezeqh7k">+44 7345 197820</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2298" data-mp="3s96xs9q4cmg8">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2299" data-mp="jsu04gdyoqm7" />
                    <span data-id="element-2300" data-mp="arw1kdmalbb0">@dentico_clinic</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-center gap-6" data-id="element-2301" data-mp="fqh5oo4o0f4q">
                <div className="flex flex-col items-center" data-id="element-2302" data-mp="fstkwtj1gbe3">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2303" data-mp="2vhbt5atc541p">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR" className="w-full h-full object-contain" data-id="element-2304" data-mp="111egnqpsnjwi" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2305" data-mp="nc2twlnmu82m">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2306" data-mp="ib5bc7ypnc0l" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2307" data-mp="9bic9stgeiap">
                      Follow us
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center" data-id="element-2308" data-mp="3llbllarqad8q">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2309" data-mp="3qlhc2nm9htp8">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR" className="w-full h-full object-contain" data-id="element-2310" data-mp="37lwobvugkx9" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2311" data-mp="3gn9xgbfg3nqe">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2312" data-mp="3gl70t8jsewcp" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2313" data-mp="vv7vl6u4n1ds">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* PRINT STYLES                               */}
      {/* ═══════════════════════════════════════════ */}
      <style data-id="element-2314" data-mp="18ggb2hprcb3h">{`
        /* ── Screen preview ── */
        @media screen {
          .print-panel {
            width: 100%;
            max-width: 148.5mm;
            aspect-ratio: 148.5 / 210;
          }
          .print-panel-single {
            width: 100%;
            max-width: 297mm;
            aspect-ratio: 297 / 210;
          }
        }

        /* ── Print: shared rules ── */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-panel {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
        }

        /* ── Print FRONT only ── */
        @media print {
          .print-mode-front .back-panel {
            display: none !important;
          }
          .print-mode-front .front-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-front {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BACK only ── */
        @media print {
          .print-mode-back .front-panel {
            display: none !important;
          }
          .print-mode-back .back-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-back {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BOTH (A4 Landscape, side by side) ── */
        @media print {
          .print-mode-both {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print-mode-both .front-panel,
          .print-mode-both .back-panel {
            width: 148.5mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            display: inline-block !important;
            vertical-align: top !important;
            float: left !important;
          }
          .print-mode-both .single-panel {
            display: none !important;
          }
        }

        /* ── Print SINGLE (A4 Landscape, both sides on one page) ── */
        @media print {
          .print-mode-single .front-panel,
          .print-mode-single .back-panel {
            display: none !important;
          }
          .print-mode-single .single-panel {
            width: 297mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            display: flex !important;
          }
          .print-mode-single {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Hide single panel in front/back modes ── */
        @media print {
          .print-mode-front .single-panel,
          .print-mode-back .single-panel {
            display: none !important;
          }
        }

        /* ── @page rules per mode ── */
        .print-mode-front ~ style,
        .print-mode-back ~ style {
          /* Handled by dynamic @page below */
        }
      `}</style>

      {/* Dynamic @page size based on print mode */}
      {printMode === 'front' || printMode === 'back' ? <style data-id="element-2315" data-mp="9p71gpts6xl4">{`
          @media print {
            @page {
              size: 148mm 210mm;
              margin: 0;
            }
          }
        `}</style> : printMode === 'both' ? <style data-id="element-2316" data-mp="d74hq3k7fsx8">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : printMode === 'single' ? <style data-id="element-2317" data-mp="3a9o9z2sgipt">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : null}
    </div>;
}